/**
 * The exam the learner is working on (D-105, D-109): TCF Canada, IELTS General Training or TEF
 * Canada. The choice is
 * remembered on this device; every learner route then receives `exam` (TCF stays the default and
 * keeps its calls unchanged: no parameter is sent for it).
 */

import { ExamEnum, useAcademyMeRetrieve } from "@agencyos/api-client";
import { openExams } from "@agencyos/core";
import { Menu } from "@agencyos/ui-web";
import { Check, ChevronDown, Languages } from "lucide-react";
import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { readStorage, writeStorage } from "../../lib/storage";

export type ExamCode = (typeof ExamEnum)[keyof typeof ExamEnum];
export const IELTS: ExamCode = ExamEnum.ielts_general;
export const TCF: ExamCode = ExamEnum.tcf_canada;
export const TEF: ExamCode = ExamEnum.tef_canada;
/** German, Goethe-Zertifikat A1 to C2 (D-118). */
export const GOETHE_CODE: ExamCode = ExamEnum.goethe;
export const EXAMS: ExamCode[] = [TCF, IELTS, TEF, GOETHE_CODE];

const STORAGE_KEY = "academy.exam";

function stored(): ExamCode {
  const value = readStorage(STORAGE_KEY);
  return value === IELTS ? IELTS : value === TEF ? TEF : value === GOETHE_CODE ? GOETHE_CODE : TCF;
}

interface ExamContextValue {
  exam: ExamCode;
  setExam: (exam: ExamCode) => void;
}

const ExamContext = createContext<ExamContextValue>({ exam: TCF, setExam: () => undefined });

export function ExamProvider({ children }: { children: ReactNode }) {
  const [exam, setState] = useState<ExamCode>(stored);
  const setExam = useCallback((next: ExamCode) => {
    writeStorage(STORAGE_KEY, next);
    setState(next);
  }, []);
  const value = useMemo(() => ({ exam, setExam }), [exam, setExam]);
  return <ExamContext.Provider value={value}>{children}</ExamContext.Provider>;
}

/** D-123: a learner who follows one exam only (and never chose one on this device) lands on it:
 * a learner of the TEF, the IELTS or German alone opened on the TCF home. */
export function useOnlyExam(exams: unknown): void {
  const { exam, setExam } = useContext(ExamContext);
  const list = (Array.isArray(exams) ? exams : []).filter((code): code is ExamCode => (EXAMS as unknown[]).includes(code));
  const only = list.length === 1 ? list[0] : undefined;
  useEffect(() => {
    if (only && only !== exam && readStorage(STORAGE_KEY) === null) setExam(only);
  }, [only, exam, setExam]);
}

export function useActiveExam(): ExamContextValue {
  return useContext(ExamContext);
}

export function useIsIelts(): boolean {
  return useActiveExam().exam === IELTS;
}

export function useIsTef(): boolean {
  return useActiveExam().exam === TEF;
}

/** The exam whose course is shown: the active exam itself (TCF, TEF, IELTS or German), the TCF
 * course only for an unknown code. */
export function courseExam(exam: string): ExamCode {
  return (EXAMS as string[]).includes(exam) ? (exam as ExamCode) : TCF;
}

/** D-123: the « every score is an estimate » notice of an exam (it named the TCF for every exam). */
export function estimateKey(exam: string): string {
  return exam === IELTS ? "ielts.estimate" : exam === TEF ? "tef.estimateLong" : exam === GOETHE_CODE ? "goethe.estimateLong" : "academy.estimateNotice";
}

/** D-123: the language of an exam's own texts (titles, themes), for the `lang` attribute. */
export { contentLanguage } from "@agencyos/core";

export function useIsGoethe(): boolean {
  return useActiveExam().exam === GOETHE_CODE;
}

/** `?exam=tef_canada` on the lesson routes while the learner works on the TEF (a lesson shared
 * with the TCF path then counts for the TEF, D-109); nothing otherwise. */
export function useLessonExamParams(): { exam: ExamCode } | undefined {
  const { exam } = useActiveExam();
  return exam === TEF || exam === GOETHE_CODE ? { exam } : undefined;
}

/** Query parameters of a learner route: nothing for the TCF (unchanged calls), `exam` otherwise. */
export { examParams } from "@agencyos/core";

/** Chooses the screen of the active exam (the TEF screen, when given, for the TEF). */
export function ByExam({ tcf, ielts, tef, goethe }: { tcf: ReactNode; ielts: ReactNode; tef?: ReactNode; goethe?: ReactNode }) {
  const { exam } = useActiveExam();
  if (exam === GOETHE_CODE && goethe !== undefined) return <>{goethe}</>;
  return <>{exam === IELTS ? ielts : exam === TEF && tef !== undefined ? tef : tcf}</>;
}

/** Exam picker of the top bar: a small capsule with the active exam, a menu with both. */
export function ExamSwitcher() {
  const { t } = useTranslation();
  const { exam, setExam } = useActiveExam();
  // D-120: only the exams opened to the learner (all of them while they have no access);
  // D-160: within the student space (the exam chosen at sign-up, or set by the agency)
  const me = useAcademyMeRetrieve({ query: { retry: false, staleTime: 60_000 } });
  const learner = me.data?.status === 200 ? me.data.data : undefined;
  const exams = useMemo<ExamCode[]>(() => (learner ? EXAMS.filter((code) => openExams(learner).includes(code)) : EXAMS), [learner]);
  useEffect(() => {
    const first = exams[0];
    if (learner && first && !exams.includes(exam)) setExam(first);
  }, [learner, exams, exam, setExam]);
  if (learner && exams.length === 1) {
    // D-160: one exam only: its name, never a menu offering the others
    return (
      <span className="ac-exam-switch ac-exam-switch--single">
        <Languages aria-hidden size={15} strokeWidth={1.9} />
        <span className="ac-exam-switch__name">{t(`ielts.exams.${exams[0]}.short`)}</span>
      </span>
    );
  }
  return (
    <Menu
      label={t("ielts.switcher.label")}
      align="end"
      triggerClassName="ac-exam-switch"
      trigger={
        <>
          <Languages aria-hidden size={15} strokeWidth={1.9} />
          <span className="ac-exam-switch__name">{t(`ielts.exams.${exam}.short`)}</span>
          <ChevronDown aria-hidden size={14} strokeWidth={2} />
        </>
      }
      items={[
        { heading: t("ielts.switcher.heading") },
        ...exams.map((code) => ({
          label: t(`ielts.exams.${code}.name`),
          icon: code === exam ? <Check aria-hidden size={16} /> : <span className="ac-exam-switch__blank" aria-hidden="true" />,
          onSelect: () => setExam(code),
        })),
      ]}
    />
  );
}
